import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query'
import { type RootState } from '@wellesley/store'
import { waitUntilAllowed } from '../waitUntilAllowed'

export type BQuery = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
>

const isDev = process.env.NODE_ENV === 'development'

const MAX_RETRIES = isDev ? 0 : 5

// Status codes where the request itself is the problem — retrying won't help
const NON_RETRYABLE_STATUSES = new Set([
  400, // Bad Request — malformed payload
  401, // Unauthorized — invalid auth
  403, // Forbidden — not allowed
  404, // Not Found — wrong URL or missing resource
  409, // Conflict — business rule conflict
  422, // Unprocessable Entity — validation failed
  501, // Not Implemented — server doesn't support this method
  505, // HTTP Version Not Supported — protocol mismatch
])

// Server errors that are typically transient
const RETRYABLE_SERVER_ERRORS = new Set([500, 502, 503, 504])

// HTTP methods that are idempotent (safe to repeat without side effects)
const IDEMPOTENT_METHODS = new Set(['GET', 'PUT', 'DELETE', 'HEAD', 'OPTIONS'])

export const getHttpMethod = (args: string | FetchArgs): string => {
  if (typeof args === 'string') return 'GET'
  return (args.method || 'GET').toUpperCase()
}

const isIdempotent = (args: string | FetchArgs): boolean =>
  IDEMPOTENT_METHODS.has(getHttpMethod(args))

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Exponential backoff with jitter to prevent thundering herd
const getBackoffDelay = (attempt: number): number => {
  const baseDelay = Math.pow(2, attempt) * 1000
  const jitter = Math.random() * 1000
  return baseDelay + jitter
}

interface RetryOptions {
  maxRetries?: number
}

export const baseQueryRetry =
  (
    baseQuery: BQuery,
  ): BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    RetryOptions,
    FetchBaseQueryMeta
  > =>
  async (args, baseQueryApi, extraOptions) => {
    const maxRetries = extraOptions?.maxRetries ?? MAX_RETRIES
    await waitUntilAllowed(
      () => (baseQueryApi.getState() as RootState).network.isOnline,
    )

    let attempt = 0
    const idempotentRequest = isIdempotent(args)

    const retry = async (): Promise<ReturnType<BQuery>> => {
      const response = await baseQuery(args, baseQueryApi, extraOptions)
      const error = response.error

      if (!error) return response

      const status = error.status

      // Never retry non-retryable status codes
      if (typeof status === 'number' && NON_RETRYABLE_STATUSES.has(status)) {
        return response
      }

      if (attempt >= maxRetries) return response

      // 429 Too Many Requests — safe for all methods since the server
      // rejected without processing. Honor Retry-After, fall back to backoff.
      if (status === 429) {
        attempt++
        const retryAfter = response.meta?.response?.headers?.get('Retry-After')
        const retryDelay = retryAfter
          ? Number(retryAfter) * 1000
          : getBackoffDelay(attempt)
        await delay(retryDelay)
        return retry()
      }

      // For non-idempotent requests (POST, PATCH), don't retry on ambiguous
      // failures — the request may have been processed server-side
      if (!idempotentRequest) return response

      // Below: only idempotent requests (GET, PUT, DELETE, HEAD, OPTIONS)

      // Network-level failures
      if (!navigator.onLine || status === 'FETCH_ERROR') {
        attempt++
        await delay(getBackoffDelay(attempt))
        return retry()
      }

      // 408 Request Timeout — server timed out waiting for request
      if (status === 408) {
        attempt++
        await delay(getBackoffDelay(attempt))
        return retry()
      }

      // Retryable server errors (500, 502, 503, 504)
      if (typeof status === 'number' && RETRYABLE_SERVER_ERRORS.has(status)) {
        attempt++
        await delay(getBackoffDelay(attempt))
        return retry()
      }

      return response
    }

    return retry()
  }
